import React, { Component } from "react";
import ReactFlexyTable from "react-flexy-table";
import "react-flexy-table/dist/index.css";
import DatePicker from "antd/lib/date-picker";
import moment from "moment";
import Select from "react-select";
import {
  ToastsContainer,
  ToastsContainerPosition,
  ToastsStore,
} from "react-toasts";
import {
  getAiVerificationReports,
} from "../../utils/aiDocumentVerification";
import {
  downloadReportRowsSampleCsv,
  downloadVerificationErrorsCsv,
} from "./aiVerificationCsvExport";
import { downloadReportSummaryExcel } from "./aiVerificationExcelExport";
import { buildReportExportFilename, getRunErrorDetailLines } from "./aiVerificationReportFormat";
import "./document-verification.css";

interface AllProps {
  history: any;
}

interface State {
  loading: boolean;
  reportType: string;
  dateFrom: string;
  dateTo: string;
  processType: string;
  status: string;
  fileName: string;
  summary: any;
  fileDetails: any[];
  rows: any[];
  hasRealData: boolean;
  page: number;
  total: number;
  totalPages: number;
}

const REPORT_TYPES = [
  { value: "summary", label: "Summary" },
  { value: "file_wise", label: "File-wise" },
  { value: "user_wise", label: "User-wise" },
  { value: "process_wise", label: "Process-wise" },
  { value: "error_details", label: "Error Details" },
  { value: "verification_status", label: "Verification Status" },
];

const REPORT_TYPE_OPTIONS = REPORT_TYPES.map((item) => ({
  value: item.value,
  label: item.label,
}));

const PROCESS_TYPE_OPTIONS = [
  { value: "", label: "All Process Types" },
  { value: "process", label: "Process" },
  { value: "verification", label: "Verification" },
  { value: "rectification", label: "Rectification" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed" },
];

const REPORT_COLUMN_HEADERS: Record<string, string> = {
  page_line: "Page No. / Line No.",
  index: "S.No",
};

const HIDDEN_REPORT_COLUMNS = new Set([
  "raw_response",
  "document_url",
  "page_number",
  "line_number",
  "id",
  "run_id",
]);

const REPORT_DATE_RANGE_MONTHS = 3;
const REPORT_PAGE_SIZE = 20;

const SUMMARY_STAT_CARDS: {
  label: string;
  key: string;
  tone: string;
  format?: (value: unknown) => string | number;
}[] = [
  { label: "Files Verified", key: "files_verified", tone: "info" },
  { label: "Total Runs", key: "total_runs", tone: "primary" },
  { label: "Completed Runs", key: "completed_runs", tone: "success" },
  { label: "Failed Runs", key: "failed_runs", tone: "danger" },
  { label: "Errors Found", key: "total_errors_found", tone: "warning" },
  { label: "Open Errors", key: "open_errors", tone: "danger" },
  {
    label: "Total Tokens",
    key: "total_tokens",
    tone: "teal",
    format: (value) => formatTokenCount(value),
  },
  {
    label: "Avg Response (ms)",
    key: "avg_response_time_ms",
    tone: "secondary",
    format: (value) => Math.round(Number(value || 0)),
  },
];

function formatDateInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getTodayDateInput(): string {
  return formatDateInput(new Date());
}

function getMinReportDateInput(): string {
  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);
  minDate.setMonth(minDate.getMonth() - REPORT_DATE_RANGE_MONTHS);
  return formatDateInput(minDate);
}

function clampReportDate(value: string): string {
  if (!value) return "";
  const min = getMinReportDateInput();
  const max = getTodayDateInput();
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function getInitialReportDates(): { dateFrom: string; dateTo: string } {
  const today = getTodayDateInput();
  return { dateFrom: today, dateTo: today };
}

function reportTypeShowsErrorStatusFilter(reportType: string): boolean {
  return reportType === "error_details";
}

function normalizeReportRow(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(row || {}).forEach(([key, value]) => {
    out[key] = value == null ? "" : value;
  });
  return out;
}

function mapRowsWithIndex(rows: any[], baseIndex = 0): any[] {
  return rows.map((row, index) => ({
    index: baseIndex + index + 1,
    ...normalizeReportRow(row),
  }));
}

function getReportTableColumnKeys(row: Record<string, unknown> | undefined): string[] {
  if (!row) return [];
  const keys = Object.keys(row).filter((k) => !HIDDEN_REPORT_COLUMNS.has(k));
  if (keys.includes("index")) {
    return ["index", ...keys.filter((k) => k !== "index")];
  }
  return keys;
}

function formatTokenCount(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "0";
  return n.toLocaleString();
}

export default class AiVerificationReportPage extends Component<AllProps, State> {
  state: State = {
    loading: false,
    reportType: "summary",
    ...getInitialReportDates(),
    processType: "",
    status: "",
    fileName: "",
    summary: null,
    fileDetails: [],
    rows: [],
    hasRealData: false,
    page: 1,
    total: 0,
    totalPages: 0,
  };

  componentDidMount() {
    this.loadReport();
  }

  buildReportPayload = (downloadAll = false) => {
    let { reportType, dateFrom, dateTo, processType, status, fileName, page } = this.state;

    dateFrom = clampReportDate(dateFrom);
    dateTo = clampReportDate(dateTo);
    if (dateFrom && dateTo && dateFrom > dateTo) {
      dateTo = dateFrom;
    }

    return {
      report_type: reportType,
      date_from: dateFrom,
      date_to: dateTo,
      process_type: processType,
      status,
      file_name: fileName,
      page: downloadAll ? 1 : page,
      page_size: REPORT_PAGE_SIZE,
      download_all: downloadAll ? 1 : 0,
    };
  };

  loadReport = (resetPage = false) => {
    if (resetPage && this.state.page !== 1) {
      this.setState({ page: 1 }, () => this.loadReport(false));
      return;
    }

    const payload = this.buildReportPayload(false);
    const { reportType } = this.state;

    if (
      payload.date_from !== this.state.dateFrom ||
      payload.date_to !== this.state.dateTo
    ) {
      this.setState({
        dateFrom: payload.date_from,
        dateTo: payload.date_to,
      });
    }

    this.setState({ loading: true });

    getAiVerificationReports(payload)
      .then((response) => {
        const pagination = response?.pagination || {};
        const total = Number(pagination.total) || 0;
        const totalPages = Number(pagination.total_pages) || 0;

        if (!response?.success) {
          ToastsStore.error(response?.message || "Failed to load report");
          this.setState({
            summary: reportType === "summary" ? {} : null,
            fileDetails: [],
            rows: [],
            hasRealData: false,
            total: 0,
            totalPages: 0,
            loading: false,
          });
          return;
        }

        if (reportType === "summary") {
          const { file_details: fileDetailsFromApi, ...summaryStats } = response.data || {};
          this.setState({
            summary: summaryStats,
            fileDetails: fileDetailsFromApi || [],
            rows: [],
            hasRealData: total > 0 || Number(summaryStats.total_runs) > 0,
            total,
            totalPages,
            loading: false,
          });
          return;
        }

        this.setState({
          rows: mapRowsWithIndex(
            Array.isArray(response.data) ? response.data : [],
            (this.state.page - 1) * REPORT_PAGE_SIZE,
          ),
          summary: null,
          fileDetails: [],
          hasRealData: total > 0,
          total,
          totalPages,
          loading: false,
        });
      })
      .catch(() => {
        ToastsStore.error("Failed to load report");
        this.setState({
          summary: reportType === "summary" ? {} : null,
          fileDetails: [],
          rows: [],
          hasRealData: false,
          total: 0,
          totalPages: 0,
          loading: false,
        });
      });
  };

  handleReportTypeChange = (reportType: string) => {
    const nextStatus = reportTypeShowsErrorStatusFilter(reportType) ? this.state.status : "";
    this.setState({ reportType, status: nextStatus, page: 1 }, () => this.loadReport());
  };

  handleProcessTypeChange = (processType: string) => {
    this.setState({ processType, page: 1 }, () => this.loadReport());
  };

  handleStatusChange = (status: string) => {
    this.setState({ status, page: 1 }, () => this.loadReport());
  };

  handleDateFromChange = (value: string) => {
    const dateFrom = clampReportDate(value);
    this.setState((prev) => ({
      dateFrom,
      dateTo:
        dateFrom && prev.dateTo && dateFrom > prev.dateTo ? dateFrom : prev.dateTo,
    }));
  };

  handleDateToChange = (value: string) => {
    const dateTo = clampReportDate(value);
    this.setState((prev) => ({
      dateTo,
      dateFrom:
        dateTo && prev.dateFrom && dateTo < prev.dateFrom ? dateTo : prev.dateFrom,
    }));
  };

  disabledReportDate = (current: moment.Moment | null) => {
    if (!current) return false;
    return (
      current > moment().endOf("day") ||
      current < moment().subtract(REPORT_DATE_RANGE_MONTHS, "months").startOf("day")
    );
  };

  handleDatePickerChange = (type: "dateFrom" | "dateTo", date: moment.Moment | null) => {
    const value = date ? date.format("YYYY-MM-DD") : "";
    if (type === "dateFrom") {
      this.handleDateFromChange(value);
      return;
    }
    this.handleDateToChange(value);
  };

  findSelectOption = (
    options: { value: string; label: string }[],
    value: string,
  ) => options.find((option) => option.value === value) || null;

  exportReport = async () => {
    const { reportType } = this.state;

    if (!this.state.hasRealData) {
      ToastsStore.warning("No report data to export.");
      return;
    }

    try {
      const response = await getAiVerificationReports(this.buildReportPayload(true));
      if (!response?.success) {
        ToastsStore.error(response?.message || "Failed to load report for export");
        return;
      }

      if (reportType === "summary") {
        const { file_details: fileDetailsFromApi, ...summaryStats } = response.data || {};
        await downloadReportSummaryExcel(
          summaryStats,
          fileDetailsFromApi || [],
          buildReportExportFilename("system_verification_report_summary", "xlsx"),
        );
        ToastsStore.success("Report Excel downloaded");
        return;
      }

      const exportRows = mapRowsWithIndex(Array.isArray(response.data) ? response.data : []);

      if (reportType === "error_details") {
        downloadVerificationErrorsCsv(
          exportRows as any,
          buildReportExportFilename("system_verification_error_details", "csv"),
        );
        ToastsStore.success("Report CSV downloaded");
        return;
      }

      if (exportRows.length) {
        downloadReportRowsSampleCsv(
          exportRows,
          reportType,
          buildReportExportFilename(`system_verification_report_${reportType}`, "csv"),
        );
        ToastsStore.success("Report CSV downloaded");
        return;
      }

      ToastsStore.warning("No report data to export.");
    } catch {
      ToastsStore.error("Download failed");
    }
  };

  renderRunStatusBadge(status: string) {
    const normalized = String(status || "").toLowerCase();
    const cls =
      normalized === "completed"
        ? "ai-status-badge ai-status-badge--completed"
        : normalized === "failed"
        ? "ai-status-badge ai-status-badge--failed"
        : normalized === "running"
        ? "ai-status-badge ai-status-badge--running"
        : "ai-status-badge ai-status-badge--default";
    return <span className={cls}>{status || "—"}</span>;
  }

  renderRunErrorDetailsCell(row: Record<string, unknown>) {
    const lines = getRunErrorDetailLines(row);
    if (!lines.length) {
      return <span className="text-muted">—</span>;
    }
    const isFailedOnly =
      String(row.run_status ?? row.last_run_status ?? "").toLowerCase() === "failed" &&
      !((row.error_items as unknown[]) || []).length;
    return (
      <div className="ai-file-error-lines">
        {lines.map((line, i) => (
          <div
            key={i}
            className={isFailedOnly ? "ai-run-failed-reason text-danger" : "ai-file-error-line"}
          >
            {line}
          </div>
        ))}
      </div>
    );
  }

  renderReportPagination() {
    const { page, totalPages, total } = this.state;
    if (totalPages <= 1) {
      return total > 0 ? (
        <small className="text-muted d-block mt-2">
          Showing {total} record{total !== 1 ? "s" : ""}
        </small>
      ) : null;
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Page {page} of {totalPages} ({total} records)
        </small>
        <div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mr-1"
            disabled={page <= 1 || this.state.loading}
            onClick={() => this.setState({ page: page - 1 }, () => this.loadReport())}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={page >= totalPages || this.state.loading}
            onClick={() => this.setState({ page: page + 1 }, () => this.loadReport())}
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  renderFileDetailsTable() {
    const { fileDetails, page } = this.state;
    const rowOffset = (page - 1) * REPORT_PAGE_SIZE;

    if (!fileDetails.length) {
      return (
        <>
          <p className="text-muted mb-0 py-2 small">
            No file verification records for the selected filters.
          </p>
          {this.renderReportPagination()}
        </>
      );
    }

    return (
      <div className="table-responsive ai-file-details-table-wrap">
        <table className="table table-sm table-bordered ai-file-details-table mb-0">
          <thead>
            <tr>
              <th>#</th>
              <th>File ID</th>
              <th>File Name</th>
              <th>Process Type</th>
              <th>Run Status</th>
              <th>Tokens</th>
              <th>Total Errors</th>
              <th>Completed Errors</th>
              <th>Not Completed Errors</th>
              <th>Error Details (with Completion Status)</th>
              <th>Run At</th>
            </tr>
          </thead>
          <tbody>
            {fileDetails.map((row, index) => {
              const notCompleted =
                row.not_completed_errors_count ?? row.open_errors ?? 0;
              const completed =
                row.completed_errors_count ??
                (Number(row.total_errors || 0) - Number(notCompleted));
              const runStatus = row.run_status ?? row.last_run_status;
              return (
              <tr
                key={`${row.file_idfk}-${row.last_run_at}-${index}`}
                className={
                  Number(notCompleted) > 0
                    ? "ai-file-details-row--has-open"
                    : runStatus === "failed"
                    ? "ai-file-details-row--failed"
                    : ""
                }
              >
                <td>{rowOffset + index + 1}</td>
                <td className="font-weight-bold">{row.file_idfk}</td>
                <td className="small">{row.file_name || "—"}</td>
                <td>
                  <span className="ai-process-pill">
                    {row.process_type || row.last_process_type || "—"}
                  </span>
                </td>
                <td>{this.renderRunStatusBadge(runStatus)}</td>
                <td className="text-nowrap">{formatTokenCount(row.total_tokens)}</td>
                <td>{row.total_errors ?? 0}</td>
                <td>
                  <span className="text-success font-weight-bold">{completed}</span>
                </td>
                <td>
                  <span
                    className={
                      Number(notCompleted) > 0
                        ? "ai-open-errors-count"
                        : "text-muted"
                    }
                  >
                    {notCompleted}
                  </span>
                </td>
                <td className="ai-file-error-details-cell small">
                  {this.renderRunErrorDetailsCell(row)}
                </td>
                <td className="text-nowrap small">{row.last_run_at || "—"}</td>
              </tr>
            );
            })}
          </tbody>
        </table>
        {this.renderReportPagination()}
      </div>
    );
  }

  renderSummary() {
    const s = this.state.summary;
    if (!s) return null;
    return (
      <>
        <div className="ai-summary-grid">
          {SUMMARY_STAT_CARDS.map(({ label, key, tone, format }) => {
            const raw = s[key];
            const value = format ? format(raw) : raw ?? 0;
            return (
              <div
                className={`ai-verification-stat ai-verification-stat--${tone}`}
                key={key}
              >
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            );
          })}
        </div>
        <div className="ai-file-details-section">
          <h6 className="ai-file-details-title">File Verification Status</h6>
          {this.renderFileDetailsTable()}
        </div>
      </>
    );
  }

  render() {
    const {
      loading,
      reportType,
      dateFrom,
      dateTo,
      processType,
      status,
      fileName,
      rows,
    } = this.state;

    const columns = rows.length
      ? getReportTableColumnKeys(rows[0]).map((key) => ({
            header:
              REPORT_COLUMN_HEADERS[key] ||
              key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            key,
            sortable: key !== "index",
            td: (row: Record<string, unknown>) => {
              const val = row[key];
              return val == null ? "" : String(val);
            },
          }))
      : [];

    const hasTableData = reportType !== "summary" && rows.length > 0;
    const hasSummaryData = reportType === "summary" && this.state.summary !== null;

    return (
      <section className="pcoded-main-container ai-verification-page-shell">
        <ToastsContainer store={ToastsStore} position={ToastsContainerPosition.TOP_RIGHT} />
        <div className="pcoded-wrapper">
          <div className="pcoded-content">
            <div className="pcoded-inner-content">
              <div className="main-body">
                <div className="page-wrapper">
                  <div className="row mt-3">
                    <div className="col-sm-12">
                      <div className="card ai-admin-card">
                        <div className="card-header ai-admin-page-header">
                          <div className="ai-admin-page-title">
                            <h5>System Verification Report</h5>
                            <p className="text-muted mb-0 small">
                              Summary and detailed reports for system document verification runs.
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm mt-1"
                            onClick={this.exportReport}
                            disabled={!this.state.hasRealData}
                          >
                            {reportType === "summary" ? "Download Excel" : "Download CSV"}
                          </button>
                        </div>
                        <div className="card-body">
                          <div className="ai-report-filters">
                            <div className="row ai-report-filter-row">
                              <div className="col-xl-2 col-md-4 col-sm-6 ai-report-filter-col">
                                <h6>Report Type</h6>
                                <Select
                                  options={REPORT_TYPE_OPTIONS}
                                  value={this.findSelectOption(REPORT_TYPE_OPTIONS, reportType)}
                                  onChange={(option: { value: string; label: string } | null) =>
                                    this.handleReportTypeChange(option?.value || "summary")
                                  }
                                  className="ai-report-select"
                                  classNamePrefix="ai-report-select"
                                  isSearchable={false}
                                />
                              </div>
                              <div className="col-xl-2 col-md-4 col-sm-6 ai-report-filter-col">
                                <h6>From Date</h6>
                                <DatePicker
                                  format="DD.MM.YYYY"
                                  className="ai-report-filter-datepicker"
                                  disabledDate={this.disabledReportDate}
                                  value={dateFrom ? moment(dateFrom, "YYYY-MM-DD") : undefined}
                                  onChange={(date) => this.handleDatePickerChange("dateFrom", date)}
                                  style={{ height: "39px", width: "100%" }}
                                />
                              </div>
                              <div className="col-xl-2 col-md-4 col-sm-6 ai-report-filter-col">
                                <h6>To Date</h6>
                                <DatePicker
                                  format="DD.MM.YYYY"
                                  className="ai-report-filter-datepicker"
                                  disabledDate={this.disabledReportDate}
                                  value={dateTo ? moment(dateTo, "YYYY-MM-DD") : undefined}
                                  onChange={(date) => this.handleDatePickerChange("dateTo", date)}
                                  style={{ height: "39px", width: "100%" }}
                                />
                              </div>
                              <div className="col-xl-2 col-md-4 col-sm-6 ai-report-filter-col">
                                <h6>Process Type</h6>
                                <Select
                                  options={PROCESS_TYPE_OPTIONS}
                                  value={this.findSelectOption(PROCESS_TYPE_OPTIONS, processType)}
                                  onChange={(option: { value: string; label: string } | null) =>
                                    this.handleProcessTypeChange(option?.value || "")
                                  }
                                  className="ai-report-select"
                                  classNamePrefix="ai-report-select"
                                  isSearchable={false}
                                />
                              </div>
                              {reportTypeShowsErrorStatusFilter(reportType) ? (
                                <div className="col-xl-2 col-md-4 col-sm-6 ai-report-filter-col">
                                  <h6>Status</h6>
                                  <Select
                                    options={STATUS_OPTIONS}
                                    value={this.findSelectOption(STATUS_OPTIONS, status)}
                                    onChange={(option: { value: string; label: string } | null) =>
                                      this.handleStatusChange(option?.value || "")
                                    }
                                    className="ai-report-select"
                                    classNamePrefix="ai-report-select"
                                    isSearchable={false}
                                  />
                                </div>
                              ) : null}
                              <div className="col-xl-2 col-md-4 col-sm-6 ai-report-filter-col">
                                <h6>File Name / ID</h6>
                                <input
                                  type="text"
                                  className="form-control ai-report-text-input"
                                  placeholder="Search file..."
                                  value={fileName}
                                  onChange={(e) => this.setState({ fileName: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      this.loadReport(true);
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-xl-2 col-md-4 col-sm-6 ai-report-filter-col ai-report-filter-action-col">
                                <h6>&nbsp;</h6>
                                <button
                                  type="button"
                                  className="btn btn-primary ai-report-apply-btn"
                                  onClick={() => this.loadReport(true)}
                                  disabled={loading}
                                >
                                  {loading ? <i className="fa fa-spinner fa-spin" /> : "Apply Filters"}
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="ai-report-results">
                            {loading ? (
                              <div className="ai-admin-loading">
                                <i className="fa fa-spinner fa-spin fa-2x" />
                                <p className="text-muted mt-2 mb-0">Loading report...</p>
                              </div>
                            ) : hasSummaryData ? (
                              this.renderSummary()
                            ) : hasTableData ? (
                              <>
                                <ReactFlexyTable
                                  key={`${reportType}-${status}-${processType}-${dateFrom}-${dateTo}-${fileName}-${this.state.page}`}
                                  data={rows}
                                  columns={columns}
                                  sortable
                                  globalSearch
                                  pageSize={Math.max(rows.length, REPORT_PAGE_SIZE)}
                                />
                                {this.renderReportPagination()}
                              </>
                            ) : this.state.total > 0 ? (
                              <>
                                <p className="text-muted mb-0 py-3 text-center">
                                  No records on this page.
                                </p>
                                {this.renderReportPagination()}
                              </>
                            ) : (
                              <p className="text-muted mb-0 py-3 text-center">
                                No records found for selected filters.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}
